/**
 * In-app routing for a clicked push notification (TRUS-668).
 *
 * The service worker never reloads a WeaveIn window on a click — a reload would
 * drop a call in progress. It focuses the main window and posts
 * `{type: 'weavein:navigate', url}` with a MessagePort; the window routes in-app
 * (no page load) and replies `{type: 'weavein:navigate:ack', handled}`. Only when
 * nobody acks within ~1.5 s does the worker fall back to a real navigation.
 *
 * Only the top-level main window listens (see useWebPushNavigation); pop-outs
 * and iframe tabs stay silent, so the worker picks the main window.
 */
import { deepLinkTabForPath } from '@/src/platform/shared/utils/deepLinkTab';
import { isValidTabId } from '@/lib/popout';
import { isAgentConsolePath, requestFocusRcaJob } from '@/src/platform/shared/utils/agentJobLink';
import { fallbackTabForRecordPath, parseRecordLink, requestOpenRecord } from '@/lib/recordDeepLinks';

export const PUSH_NAVIGATE_TYPE = 'weavein:navigate';
export const PUSH_NAVIGATE_ACK_TYPE = 'weavein:navigate:ack';

/** Open a messaging channel in the workspace (mounted or not yet). */
export const PUSH_OPEN_CHANNEL_EVENT = 'weavein:push:open-channel';
/** A call notification was clicked: {callId, channelId}. For the call UI to act on. */
export const PUSH_OPEN_CALL_EVENT = 'weavein:push:open-call';
/** TRUS-691: a meeting notification (`?meeting=ID`) was clicked: {meetingId}. MeetingsHost opens it. */
export const PUSH_OPEN_MEETING_EVENT = 'weavein:push:open-meeting';

function announceMeeting(search: string): void {
  if (typeof window === 'undefined') return;
  const id = Number(new URLSearchParams(search).get('meeting'));
  if (Number.isInteger(id) && id > 0) {
    window.dispatchEvent(new CustomEvent(PUSH_OPEN_MEETING_EVENT, { detail: { meetingId: id } }));
  }
}

export interface PushTarget {
  path: string;
  search: string;
  channelId: number | null;
  callId: string | null;
  thread: string | null;
}

/** Parse a notification URL (same origin only). Null for anything else. */
export function parsePushTarget(url: string, origin: string): PushTarget | null {
  let u: URL;
  try {
    u = new URL(url || '/', origin);
  } catch {
    return null;
  }
  if (u.origin !== origin) return null;
  const channelRaw = u.searchParams.get('channel');
  const thread = u.searchParams.get('thread');
  const fromThread = thread && /^channel:(\d+)$/.exec(thread);
  const id = Number(channelRaw ?? (fromThread ? fromThread[1] : NaN));
  return {
    path: u.pathname.replace(/\/+$/, '') || '/',
    search: u.search,
    channelId: Number.isFinite(id) && id > 0 ? id : null,
    callId: u.searchParams.get('call'),
    thread,
  };
}

// ----------------------------------------------------------------------------
// "open this channel" hand-off to MessagingWorkspace. The workspace may not be
// mounted yet (another tab is showing), so the request is parked until it
// mounts, and also announced for a workspace that is already on screen.
// ----------------------------------------------------------------------------
// A request nobody picks up soon (e.g. the click landed in an iframe tab) must
// not open a channel out of the blue minutes later.
const PENDING_TTL_MS = 15_000;
let pendingChannelId: number | null = null;
let pendingAt = 0;

export function requestOpenChannel(channelId: number): void {
  pendingChannelId = channelId;
  pendingAt = Date.now();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(PUSH_OPEN_CHANNEL_EVENT, { detail: { channelId } }));
  }
}

/** Take (and clear) a parked channel request. */
export function takePendingOpenChannel(): number | null {
  const id = pendingChannelId;
  pendingChannelId = null;
  return id !== null && Date.now() - pendingAt <= PENDING_TTL_MS ? id : null;
}

export interface StaffRouteDeps {
  navigateTab: (tab: string) => void;
  isTriveny?: boolean;
}

/** Route a notification URL inside the staff shell. True when handled. */
export function routeStaffPushTarget(t: PushTarget, deps: StaffRouteDeps): boolean {
  const seg = t.path.replace(/^\/+/, '');
  if (seg === 'workspace-messaging' || seg === 'messaging' || seg.startsWith('messaging/')) {
    deps.navigateTab('workspace-messaging');
    const legacyId = /^messaging\/(\d+)$/.exec(seg);
    const channelId = t.channelId ?? (legacyId ? Number(legacyId[1]) : null);
    if (channelId) requestOpenChannel(channelId);
    if (t.callId && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(PUSH_OPEN_CALL_EVENT, { detail: { callId: t.callId, channelId } }));
    }
    announceMeeting(t.search);
    return true;
  }
  if (seg.startsWith('member/') || seg.startsWith('popout/')) return false;
  if (seg === '') return true; // the app root: focusing the window is enough
  // One record (TRUS-685 event links): App sets the record in its state and
  // switches tab; the page that shows it focuses it. See lib/recordDeepLinks.
  const record = parseRecordLink(t.path, t.search);
  if (record) {
    deps.navigateTab(record.tab);
    requestOpenRecord(record);
    return true;
  }
  const fallback = fallbackTabForRecordPath(t.path);
  if (fallback) {
    deps.navigateTab(fallback);
    return true;
  }
  const tab = deepLinkTabForPath(t.path, { isTriveny: deps.isTriveny }) || (isValidTabId(seg) ? seg : null);
  if (!tab) return false;
  deps.navigateTab(tab);
  // /agents?job=<id> (TRUS-684): the console opens that RCA job once it is on screen.
  if (isAgentConsolePath(t.path)) {
    const job = new URLSearchParams(t.search).get('job');
    if (job) requestFocusRcaJob(job);
  }
  return true;
}

/** Route a notification URL inside the member portal (react-router). */
export function routeMemberPushTarget(t: PushTarget, navigate: (to: string) => void): boolean {
  if (!t.path.startsWith('/member/')) return false;
  navigate(`${t.path}${t.search}`);
  if (t.callId && typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(PUSH_OPEN_CALL_EVENT, { detail: { callId: t.callId, channelId: t.channelId } }),
    );
  }
  announceMeeting(t.search);
  return true;
}

/**
 * The listener body: a `weavein:navigate` message from the service worker is
 * routed with `route` and acked on the transferred port (or back to the worker).
 */
export function handlePushNavigateMessage(
  event: MessageEvent,
  route: (url: string) => boolean,
): boolean {
  const msg = event.data as { type?: string; url?: string } | null;
  if (!msg || msg.type !== PUSH_NAVIGATE_TYPE || typeof msg.url !== 'string') return false;
  let handled = false;
  try {
    handled = route(msg.url);
  } catch (err) {
    console.warn('[pushNavigation] route failed', err);
    handled = false;
  }
  const reply = { type: PUSH_NAVIGATE_ACK_TYPE, url: msg.url, handled };
  const port = event.ports && event.ports[0];
  if (port) port.postMessage(reply);
  return handled;
}
